import React, {useState} from 'react';
import {Linking,StyleSheet, View} from 'react-native';
import {KeyboardAwareScrollView} from 'react-native-keyboard-aware-scroll-view';
import {Formik} from 'formik';
import * as Yup from 'yup';

import Button from '../../components/ui-controls/Button';
import Dialog from '../../components/ui-controls/Dialog';
import FormView from '../../components/ui-controls/form/FormView';
import Label from '../../components/ui-controls/form/Label';
import TextInput from '../../components/ui-controls/form/TextInput';
import ValidationMessage from '../../components/ui-controls/form/ValidationMessage';
import ContentView from '../../components/ui-controls/layout/ContentView';
import Text from '../../components/ui-controls/Text';
import {AppRoute} from '../../models/AppRoute';
import {RouteNavigatorScreenProps} from '../../models/navigationTypes';
import {useStores} from '../../stores/use-stores';
import theme from '../../style/theme';
import apiService from '../../transport/apiService';
import {PHONE_NUMBER_REGEX} from '../../utils/validation';

const REQUIRED_FIELD_MESSAGE = 'This is a required field';

const ticketSchema = Yup.object().shape({
  firstName: Yup.string().required(REQUIRED_FIELD_MESSAGE).nullable(),
  lastName: Yup.string().required(REQUIRED_FIELD_MESSAGE).nullable(),
  email: Yup.string()
    .email('Please enter a valid email address')
    .required(REQUIRED_FIELD_MESSAGE)
    .nullable(),
  phone: Yup.string()
    .required(REQUIRED_FIELD_MESSAGE)
    .matches(PHONE_NUMBER_REGEX, 'Please enter a valid Australian phone number')
    .nullable(),
  reviewPersonName: Yup.string().required(REQUIRED_FIELD_MESSAGE).nullable(),
  relationship: Yup.string().required(REQUIRED_FIELD_MESSAGE).nullable(),
  description: Yup.string().required(REQUIRED_FIELD_MESSAGE).nullable(),
});

const SuccessDialog = ({isVisible, onOk, submittedTicketNumber}) => (
  <Dialog
    cancelVisible={false}
    isVisible={isVisible}
    okText="CLOSE"
    onOk={onOk}
    title="Your request was successfully submitted. Thank you.">
    <View>
      <Text style={styles.dialogText}>
        Your ticket number is #{submittedTicketNumber}
      </Text>
    </View>
  </Dialog>
);

const ErrorDialog = ({isVisible, onOk}) => (
  <Dialog
    cancelVisible={false}
    isVisible={isVisible}
    okText="CLOSE"
    onOk={onOk}
    title="Sorry, we couldn't submit your ticket at this moment. Please try again later."
  />
);

const ResponsibleServiceThirdPartyRequestScreen = ({
  navigation,
}: RouteNavigatorScreenProps<AppRoute.RESPONSIBLE_SERVICE_THIRD_PARTY_REQUEST>) => {
  const {userStore} = useStores();

  const [isSubmitting, setSubmitting] = useState(false);
  const [isSuccessDialogVisible, setSuccessDialogVisible] = useState(false);
  const [isErrorDialogVisible, setErrorDialogVisible] = useState(false);
  const [submittedTicketNumber, setSubmittedTicketNumber] = useState('');

  const defaultValues = {
    firstName: userStore.firstName,
    lastName: userStore.lastName,
    email: userStore.emailAddress,
    phone: userStore.phoneNumber,
    reviewPersonName: '',
    relationship: '',
    description: '',
  };

  const handleSuccessDialogOk = () => {
    setSuccessDialogVisible(false);
    navigation.navigate(AppRoute.HOME);
  };

  const handleErrorDialogOk = () => {
    setErrorDialogVisible(false);
  };

  const onSubmit = async values => {
    try {
      setSubmitting(true);
      const response = await apiService.createThirdPartyReviewRequest(values);
      const {ticketNumber} = response;
      if (!ticketNumber) {
        console.error(
          'Request was submitted successfully but no ticket number was received',
          response,
        );
        throw new Error();
      }
      setSubmitting(false);
      setSubmittedTicketNumber(ticketNumber);
      setSuccessDialogVisible(true);
    } catch (err) {
      setSubmitting(false);
      setErrorDialogVisible(true);
    }
  };

  return (
    <KeyboardAwareScrollView
      style={{backgroundColor: 'white'}}
      keyboardShouldPersistTaps="always">
      <Formik
        initialValues={defaultValues}
        onSubmit={onSubmit}
        validationSchema={() => ticketSchema}>
        {({
          errors,
          handleBlur,
          handleChange,
          handleSubmit,
          initialValues,
          touched,
        }) => {
          const validationMessage = field => {
            return (
              <>
                {(!errors[field] || !touched[field]) && (
                  <ValidationMessage> </ValidationMessage>
                )}
                {!!(errors[field] && touched[field]) && (
                  <ValidationMessage>{errors[field]}</ValidationMessage>
                )}
              </>
            );
          };

          return (
            <ContentView testID="third-party-request-screen">
              <Text style={styles.copy}>
                We are committed to the responsible service of alcohol and care
                about the wellbeing of our customers. If you are concerned that
                someone you know may have a problem with alcohol, you can
                contact us to review their account activity by filling in the
                below form. Your information will be handled in line with our
                privacy policy.{'\n'}
                {'\n'}You can also find information on support services{' '}
                <Text
                  style={styles.textLink}
                  onPress={() =>
                    Linking.openURL(
                      'https://drinkwiseaustr.wpengine.com/drinking-and-you/support-services/#',
                    )
                  }>
                  here
                </Text>
                .
              </Text>
              <FormView>
                <View>
                  <View>
                    <Label>First name</Label>
                    <TextInput
                      autoCapitalize="words"
                      autoComplete="name-given"
                      defaultValue={initialValues.firstName}
                      error={errors.firstName}
                      onBlur={handleBlur('firstName')}
                      onChangeText={handleChange('firstName')}
                      placeholder="First name"
                      touched={touched.firstName}
                    />
                    {validationMessage('firstName')}
                  </View>
                  <View>
                    <Label>Last name</Label>
                    <TextInput
                      autoCapitalize="words"
                      autoComplete="name-family"
                      defaultValue={initialValues.lastName}
                      error={errors.lastName}
                      onBlur={handleBlur('lastName')}
                      onChangeText={handleChange('lastName')}
                      placeholder="Last name"
                      touched={touched.lastName}
                    />
                    {validationMessage('lastName')}
                  </View>
                  <View>
                    <Label>Email</Label>
                    <TextInput
                      autoCapitalize="none"
                      autoComplete="email"
                      defaultValue={initialValues.email}
                      error={errors.email}
                      keyboardType="email-address"
                      onBlur={handleBlur('email')}
                      onChangeText={handleChange('email')}
                      placeholder="Email"
                      touched={touched.email}
                    />
                    {validationMessage('email')}
                  </View>
                  <View>
                    <Label>Phone number</Label>
                    <TextInput
                      autoCapitalize="none"
                      autoComplete="tel"
                      defaultValue={initialValues.phone}
                      error={errors.phone}
                      keyboardType="number-pad"
                      onBlur={handleBlur('phone')}
                      onChangeText={handleChange('phone')}
                      placeholder="Phone number"
                      touched={touched.phone}
                    />
                    {validationMessage('phone')}
                  </View>
                  <View>
                    <Label>
                      Name of the person who you would like to request a review
                    </Label>
                    <TextInput
                      autoCapitalize="words"
                      autoComplete="off"
                      defaultValue={initialValues.reviewPersonName}
                      error={errors.reviewPersonName}
                      onBlur={handleBlur('reviewPersonName')}
                      onChangeText={handleChange('reviewPersonName')}
                      placeholder="Name"
                      touched={touched.reviewPersonName}
                    />
                    {validationMessage('reviewPersonName')}
                  </View>
                  <View>
                    <Label>Your relationship with this person</Label>
                    <TextInput
                      autoCapitalize="words"
                      autoComplete="off"
                      defaultValue={initialValues.relationship}
                      error={errors.relationship}
                      onBlur={handleBlur('relationship')}
                      onChangeText={handleChange('relationship')}
                      placeholder="Relationship"
                      touched={touched.relationship}
                    />
                    {validationMessage('relationship')}
                  </View>
                  <View>
                    <Label>Comments</Label>
                    <TextInput
                      numberOfLines={4}
                      autoCapitalize="words"
                      autoComplete="off"
                      defaultValue={initialValues.description}
                      error={errors.description}
                      multiline={true}
                      onBlur={handleBlur('description')}
                      onChangeText={handleChange('description')}
                      touched={touched.description}
                    />
                    {validationMessage('description')}
                  </View>
                </View>
                <Button
                  onPress={handleSubmit}
                  title="Submit request"
                  disabled={isSubmitting}
                />
                <SuccessDialog
                  isVisible={isSuccessDialogVisible}
                  onOk={handleSuccessDialogOk}
                  submittedTicketNumber={submittedTicketNumber}
                />
                <ErrorDialog
                  isVisible={isErrorDialogVisible}
                  onOk={handleErrorDialogOk}
                />
              </FormView>
            </ContentView>
          );
        }}
      </Formik>
    </KeyboardAwareScrollView>
  );
};

const styles = StyleSheet.create({
  dialogText: {
    fontSize: 16,
    marginBottom: 8,
    marginTop: 8,
  },
  textLink: {
    color: theme.colour.text.link.primary,
    fontSize: 16,
    fontWeight: 'bold',
  },
  copy: {
    fontSize: 16,
  },
});

export default ResponsibleServiceThirdPartyRequestScreen;
